import { useMemo } from "react";
import { X, MapPin, Truck, Fuel, Wrench, Settings, HardHat, Clock, Download, Navigation, CheckCircle2, AlertTriangle, ThumbsUp, ThumbsDown } from "lucide-react";
import { getMachineHighlight, getSiteHighlight, highlightBorder } from "@/lib/highlights";

export default function SubjectDetail({ type, subject, hubData, onClose, onExportPDF }) {
  const { sites, machines, requests, events, drivers, feedback } = hubData;

  const content = useMemo(() => {
    if (type === "site" && subject) {
      const sMachines = machines.filter(m => m.site_id === subject.id);
      const sReqs = requests.filter(r => r.site_id === subject.id);
      const sEvents = events.filter(e => e.site_id === subject.id).slice(0, 30);
      const sFeedback = (feedback || []).filter(f => f.site_id === subject.id);
      const hl = getSiteHighlight(subject, machines, requests);
      return { sMachines, sReqs, sEvents, sFeedback, hl };
    }
    if (type === "machine" && subject) {
      const site = sites.find(s => s.id === subject.site_id);
      const mReqs = requests.filter(r => r.machine_id === subject.id);
      const mEvents = events.filter(e => e.machine_id === subject.id || e.machine_number === subject.machine_number).slice(0, 30);
      const hl = getMachineHighlight(subject, requests);
      return { site, mReqs, mEvents, hl };
    }
    if (type === "driver" && subject) {
      const dEvents = events.filter(e => e.driver_name === subject).slice(0, 30);
      const dFeedback = (feedback || []).filter(f => f.driver_name === subject);
      const dDeliveries = events.filter(e => e.driver_name === subject && e.event_type === "DELIVERY");
      const totalDiesel = dDeliveries.reduce((s, e) => s + (e.volume_diesel || 0), 0);
      const totalAdblue = dDeliveries.reduce((s, e) => s + (e.volume_adblue || 0), 0);
      return { dEvents, dFeedback, dDeliveries, totalDiesel, totalAdblue };
    }
    return {};
  }, [type, subject, sites, machines, requests, events, feedback, drivers]);

  if (!subject) return null;

  return (
    <div className="fixed inset-0 z-[850] bg-white/95 overflow-y-auto" data-testid="subject-detail">
      <div className="max-w-3xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 sticky top-0 bg-white/95 backdrop-blur-sm py-4 z-10">
          <div>
            <span className="text-[9px] font-bold bg-slate-100 text-slate-400 px-2 py-0.5 rounded uppercase">{type}</span>
            <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight mt-1">
              {type === "site" ? subject.name : type === "machine" ? `${subject.machine_number} — ${subject.name}` : subject}
            </h2>
          </div>
          <div className="flex gap-2">
            <button onClick={() => onExportPDF?.()} className="p-2.5 bg-amber-50 border border-amber-200 rounded-xl text-amber-400 hover:bg-amber-500 hover:text-slate-950 transition-all" data-testid="detail-export-pdf">
              <Download className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="p-2.5 bg-slate-100 rounded-xl text-slate-400 hover:text-slate-900" data-testid="close-subject-detail">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Site Detail */}
        {type === "site" && (
          <div className="space-y-4">
            <div className={`p-4 rounded-xl border ${content.hl ? highlightBorder(content.hl) : "border-slate-200"} bg-slate-50`}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <InfoItem label="City" value={subject.city || "—"} />
                <InfoItem label="Region" value={subject.region || "—"} />
                <InfoItem label="Phase" value={subject.current_stage || "N/A"} />
                <InfoItem label="GPS" value={subject.gps_lat > 0 ? `${subject.gps_lat.toFixed(4)}, ${subject.gps_lon.toFixed(4)}` : "N/A"} />
                <InfoItem label="Machines" value={content.sMachines?.length || 0} />
                <InfoItem label="Requests" value={content.sReqs?.length || 0} />
                <InfoItem label="Driver" value={subject.main_driver || "Auto"} />
                <InfoItem label="Last Refuel" value={subject.last_refuel ? new Date(subject.last_refuel).toLocaleString() : "Never"} />
              </div>
            </div>

            <SectionTitle icon={HardHat} title={`Machines (${content.sMachines?.length})`} />
            <div className="space-y-1.5">
              {content.sMachines?.map(m => {
                const mhl = getMachineHighlight(m, requests);
                return (
                  <div key={m.id} className={`p-3 rounded-xl border flex items-center justify-between ${mhl === "red" ? "border-red-500/30 bg-red-500/5" : mhl === "green" ? "border-lime-300 bg-lime-500/5" : "border-slate-200 bg-slate-50"}`}>
                    <div className="flex items-center gap-3">
                      {mhl === "red" && <Fuel className="w-4 h-4 text-red-500" />}
                      {mhl === "green" && <CheckCircle2 className="w-4 h-4 text-lime-600" />}
                      {!mhl && <HardHat className="w-4 h-4 text-slate-400" />}
                      <span className="text-xs font-bold text-slate-800">{m.machine_number}</span>
                      <span className="text-xs text-slate-400">{m.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${m.status_type === "DEFECT" ? "bg-red-500/10 text-red-400" : m.status_type === "TRANSPORT" ? "bg-blue-500/10 text-blue-400" : m.status_type === "UPKEEP" ? "bg-purple-500/10 text-purple-400" : "bg-slate-100 text-slate-400"}`}>{m.status_type}</span>
                      <span className="text-[9px] text-slate-400">{m.fuel_level_percent}%</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <SectionTitle icon={Fuel} title={`Requests (${content.sReqs?.length})`} />
            <EventTable items={content.sReqs?.map(r => ({ time: r.created_at, type: r.request_type, detail: `${r.machine_number} — ${r.status} — ${r.urgency}`, status: r.status }))} />

            <SectionTitle icon={Clock} title="Activity Log" />
            <EventTable items={content.sEvents?.map(e => ({ time: e.timestamp, type: e.event_type, detail: e.message }))} />

            {content.sFeedback?.length > 0 && (
              <>
                <SectionTitle icon={ThumbsUp} title="Driver Feedback" />
                <EventTable items={content.sFeedback.map(f => ({ time: f.created_at, type: f.type, detail: `${f.driver_name}: ${f.incident_type || "OK"} — ${f.text || ""}`, status: f.type }))} />
              </>
            )}
          </div>
        )}

        {/* Machine Detail */}
        {type === "machine" && (
          <div className="space-y-4">
            <div className={`p-4 rounded-xl border ${content.hl === "red" ? "border-red-500/30 bg-red-500/5" : content.hl === "green" ? "border-lime-300 bg-lime-500/5" : "border-slate-200"} bg-slate-50`}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <InfoItem label="Brand" value={subject.brand || "—"} />
                <InfoItem label="Site" value={content.site?.name || "—"} />
                <InfoItem label="Status" value={subject.status_type} />
                <InfoItem label="Fuel Level" value={`${subject.fuel_level_percent}%`} />
                <InfoItem label="Category" value={subject.category_code || "—"} />
                <InfoItem label="Rental" value={subject.is_rental ? "Yes" : "No"} />
                <InfoItem label="Last Refuel" value={subject.last_refuel ? new Date(subject.last_refuel).toLocaleString() : "Never"} />
                <InfoItem label="GPS" value={subject.gps_lat > 0 ? `${subject.gps_lat.toFixed(4)}, ${subject.gps_lon.toFixed(4)}` : "N/A"} />
              </div>
            </div>

            <SectionTitle icon={Fuel} title={`Requests (${content.mReqs?.length})`} />
            <EventTable items={content.mReqs?.map(r => ({ time: r.created_at, type: r.request_type, detail: `${r.status} — ${r.urgency} — ${r.notes || ""}`, status: r.status }))} />

            <SectionTitle icon={Clock} title="Activity Log" />
            <EventTable items={content.mEvents?.map(e => ({ time: e.timestamp, type: e.event_type, detail: e.message }))} />
          </div>
        )}

        {/* Driver Detail */}
        {type === "driver" && (
          <div className="space-y-4">
            <div className="p-4 rounded-xl border border-slate-200 bg-slate-50">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <InfoItem label="Deliveries" value={content.dDeliveries?.length || 0} />
                <InfoItem label="Total Diesel" value={`${content.totalDiesel}L`} />
                <InfoItem label="Total AdBlue" value={`${content.totalAdblue}L`} />
                <InfoItem label="Feedback" value={`${content.dFeedback?.filter(f => f.type === "POSITIVE").length || 0} pos / ${content.dFeedback?.filter(f => f.type === "NEGATIVE").length || 0} neg`} />
              </div>
            </div>

            <SectionTitle icon={Clock} title="Activity Log" />
            <EventTable items={content.dEvents?.map(e => ({ time: e.timestamp, type: e.event_type, detail: e.message }))} />

            {content.dFeedback?.length > 0 && (
              <>
                <SectionTitle icon={ThumbsUp} title="Feedback" />
                <EventTable items={content.dFeedback.map(f => ({ time: f.created_at, type: f.type, detail: `${f.incident_type || "OK"} — ${f.text || ""} at ${f.site_name || ""}`, status: f.type }))} />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function InfoItem({ label, value }) {
  return (
    <div>
      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{label}</p>
      <p className="text-sm font-bold text-slate-800">{value}</p>
    </div>
  );
}

function SectionTitle({ icon: Icon, title }) {
  return (
    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2 pt-2">
      <Icon className="w-4 h-4" /> {title}
    </h3>
  );
}

function EventTable({ items }) {
  if (!items || items.length === 0) return <p className="text-xs text-slate-400 text-center py-4">No data</p>;
  return (
    <div className="space-y-1 max-h-[300px] overflow-y-auto">
      {items.map((item, i) => (
        <div key={i} className={`p-2.5 rounded-xl border flex items-start gap-3 ${
          item.status === "POSITIVE" || item.status === "DONE" ? "border-lime-500/20 bg-lime-500/5" :
          item.status === "NEGATIVE" || item.status === "URGENT" ? "border-red-500/20 bg-red-500/5" :
          "border-slate-200 bg-slate-50/80"
        }`}>
          <span className="text-[9px] font-mono text-slate-400 whitespace-nowrap mt-0.5">{item.time ? new Date(item.time).toLocaleString() : "—"}</span>
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0 ${
            item.type === "FUEL" ? "bg-lime-50 text-lime-600" :
            item.type === "DELIVERY" || item.type === "POSITIVE" ? "bg-lime-50 text-lime-600" :
            item.type === "NEGATIVE" ? "bg-red-500/10 text-red-400" :
            item.type === "TRANSPORT" ? "bg-blue-500/10 text-blue-400" :
            item.type === "REPARATION" || item.type === "MAINTENANCE" ? "bg-purple-500/10 text-purple-400" :
            "bg-slate-100 text-slate-400"
          }`}>{item.type}</span>
          <span className="text-xs text-slate-400 leading-tight">{item.detail?.substring(0, 120)}</span>
        </div>
      ))}
    </div>
  );
}
